// ============================================================
// MongoDBExamination.jsx
// ============================================================
// Junior-dev notes:
// - Automated 'mongosh' style simulation for question results.
// - Full-screen and focus-loss detection active.
// - Progress bar tracks the 30-document exam batch.
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Container,
    Typography,
    Divider,
    Paper,
    Stack,
    Button,
    IconButton,
    LinearProgress,
    Alert,
    Snackbar,
    Grid,
    RadioGroup,
    FormControlLabel as MUIFormControlLabel,
    Radio,
    TextField,
    MenuItem,
    Avatar,
} from "@mui/material";

/* Icons */
import StorageIcon from "@mui/icons-material/Storage";
import TimerIcon from "@mui/icons-material/Timer";
import GavelIcon from "@mui/icons-material/Gavel";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { FaVolumeUp, FaVolumeMute, FaShieldAlt, FaTerminal } from "react-icons/fa";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 237, 100, 0.2)",
    green: "#00ED64",
    greenBright: "#00FF6A",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    error: "#FF5252",
    inputBg: "rgba(255,255,255,0.05)",
};

const EXAM_CONFIG = {
    totalQuestions: 30,
    durationSeconds: 20 * 60, // 20 Minutes
    maxViolations: 3,
    storageKey: "mongodb_exam_last_attempt",
};

/* ── Question Bank ───────────────────────────────────────── */
const MONGODB_BANK = [
    { id: "M1", topic: "Basics", question: "Which format does MongoDB use to store data internally?", options: ["JSON", "XML", "BSON", "SQL Tables"], answerIndex: 2 },
    { id: "M2", topic: "CRUD", question: "Which method is used to insert multiple documents at once?", options: ["db.collection.add()", "db.collection.insertMany()", "db.collection.push()", "db.collection.bulk()"], answerIndex: 1 },
    { id: "M3", topic: "Aggregation", question: "Which stage in the aggregation pipeline is used to filter documents?", options: ["$filter", "$match", "$group", "$where"], answerIndex: 1 },
    { id: "M4", topic: "Scaling", question: "What is the process of partitioning data across multiple machines called?", options: ["Replication", "Indexing", "Sharding", "Aggregation"], answerIndex: 2 },
    // ... (Full bank logic would pick 30 random from here)
];

export default function MongoDBExamination() {
    const [voiceEnabled, setVoiceEnabled] = useState(true);
    const [started, setStarted] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [timeLeft, setTimeLeft] = useState(EXAM_CONFIG.durationSeconds);
    const timerRef = useRef(null);

    const [formData, setFormData] = useState({
        fullName: "",
        track: "Backend Developer",
        date: new Date().toISOString().split("T")[0],
        attemptNumber: 1,
        location: "Online Remote"
    });

    const [violations, setViolations] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [results, setResults] = useState(null);
    const [snack, setSnack] = useState({ open: false, message: "" });

    const current = questions[activeIndex];
    const progress = Math.round(((activeIndex + 1) / EXAM_CONFIG.totalQuestions) * 100);

    const speak = useCallback((text) => {
        if (!voiceEnabled || !window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(new SpeechSynthesisUtterance(text));
    }, [voiceEnabled]);

    const addViolation = useCallback((type) => {
        setViolations((prev) => {
            const next = [...prev, { type, at: new Date().toLocaleTimeString() }];
            setSnack({ open: true, message: `Security Violation: ${type}` });
            speak(`Warning. ${type} detected. Violation ${next.length} of ${EXAM_CONFIG.maxViolations}.`);
            if (next.length >= EXAM_CONFIG.maxViolations) handleSubmit("Auto-Submit: Policy Violation");
            return next;
        });
    }, [speak]);

    // Anti-Cheat Listeners
    useEffect(() => {
        if (!started || submitted) return;
        const onVisibility = () => { if (document.visibilityState !== "visible") addViolation("Tab Switch"); };
        const onBlur = () => addViolation("Window Focus Lost");
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("blur", onBlur);
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("blur", onBlur);
        };
    }, [started, submitted, addViolation]);

    const handleStart = async () => {
        const q = [...MONGODB_BANK].sort(() => 0.5 - Math.random()).slice(0, 30);
        setQuestions(q);
        setAnswers(new Array(q.length).fill(null));
        setStarted(true);
        speak(`MongoDB exam started for ${formData.fullName}. Good luck.`);

        timerRef.current = setInterval(() => setTimeLeft((t) => t - 1), 1000);
        try { if (document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen(); } catch {}
    };

    const handleSubmit = useCallback((reason = "Manual Submission") => {
        if (timerRef.current) clearInterval(timerRef.current);
        const score = questions.reduce((acc, q, i) => acc + (answers[i] === q.answerIndex ? 1 : 0), 0);
        const res = {
            percent: Math.round((score / questions.length) * 100),
            correct: score,
            total: questions.length,
            candidate: formData,
            violations: violations.length,
            reason
        };
        setResults(res);
        setSubmitted(true);
        setStarted(false);
        speak(`Exam complete. Final score: ${res.percent} percent.`);
        if (document.fullscreenElement) document.exitFullscreen();
    }, [questions, answers, formData, violations]);

    useEffect(() => { if (started && timeLeft <= 0) handleSubmit("Time Expired"); }, [timeLeft, started, handleSubmit]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 6, px: 2 }}>
            <Container maxWidth="lg">

                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                    <Box>
                        <Typography variant="h4" sx={{ color: BRAND.white, fontWeight: 900 }}>
                            MONGODB <span style={{ color: BRAND.green }}>EXAM</span>
                        </Typography>
                        <Typography variant="caption" sx={{ color: BRAND.muted }}>Professional Certification Authority</Typography>
                    </Box>
                    <IconButton onClick={() => setVoiceEnabled(!voiceEnabled)} sx={{ border: `1px solid ${BRAND.border}`, bgcolor: BRAND.surface }}>
                        {voiceEnabled ? <FaVolumeUp color={BRAND.green} /> : <FaVolumeMute color={BRAND.green} />}
                    </IconButton>
                </Stack>

                {/* 1. Candidacy Form */}
                {!started && !submitted && (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Paper sx={{ p: 4, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                                    <AssignmentIndIcon sx={{ color: BRAND.green }} />
                                    <Typography variant="h6" sx={{ color: BRAND.white }}>Candidate Registration</Typography>
                                </Stack>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}><TextField fullWidth label="Full Name" value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})} variant="filled" sx={{ bgcolor: BRAND.inputBg }} InputLabelProps={{ style: { color: BRAND.muted } }} inputProps={{ style: { color: BRAND.white } }} /></Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField select fullWidth label="Exam Track" value={formData.track} onChange={(e) => setFormData({...formData, track: e.target.value})} variant="filled" sx={{ bgcolor: BRAND.inputBg }} InputLabelProps={{ style: { color: BRAND.muted } }} inputProps={{ style: { color: BRAND.white } }}>
                                            <MenuItem value="Backend Developer">Backend Developer (Node.js)</MenuItem>
                                            <MenuItem value="Database Administrator">Database Administrator</MenuItem>
                                            <MenuItem value="Data Scientist">Data Scientist</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={6}><TextField fullWidth type="number" label="Attempt Number" value={formData.attemptNumber} onChange={(e) => setFormData({...formData, attemptNumber: e.target.value})} variant="filled" sx={{ bgcolor: BRAND.inputBg }} InputLabelProps={{ style: { color: BRAND.muted } }} inputProps={{ style: { color: BRAND.white } }} /></Grid>
                                </Grid>
                                <Button fullWidth disabled={!formData.fullName} onClick={handleStart} variant="contained" sx={{ mt: 4, py: 2, bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 900, "&:hover": { bgcolor: BRAND.greenBright } }}>Initialize BSON Exam Batch</Button>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 4, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4, height: '100%' }}>
                                <Typography variant="h6" sx={{ color: BRAND.green, mb: 2 }}>Security Protocol</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 2 }}>
                                    • 30 Document-based Questions<br/>
                                    • No Refreshing/Tab Switching<br/>
                                    • Fullscreen Monitoring Active<br/>
                                    • 20 Minute Time Limit
                                </Typography>
                                <Box sx={{ mt: 4, textAlign: 'center' }}>
                                    <FaShieldAlt size={50} color="rgba(0,237,100,0.2)" />
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {/* 2. Active Exam Interface */}
                {started && current && (
                    <Stack spacing={3}>
                        <Paper sx={{ p: 2, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                <Typography sx={{ color: BRAND.green }}>Candidate: {formData.fullName}</Typography>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <TimerIcon sx={{ color: BRAND.white, fontSize: 18 }} />
                                    <Typography sx={{ color: BRAND.white, fontWeight: 700 }}>{Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}</Typography>
                                </Stack>
                            </Stack>
                            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4, bgcolor: "rgba(255,255,255,0.05)", "& .MuiLinearProgress-bar": { bgcolor: BRAND.green } }} />
                        </Paper>

                        <Paper sx={{ p: 4, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                            <Typography variant="h6" sx={{ color: BRAND.white, mb: 4 }}>{current.question}</Typography>
                            <RadioGroup value={answers[activeIndex] ?? ""} onChange={(e) => {
                                const next = [...answers];
                                next[activeIndex] = Number(e.target.value);
                                setAnswers(next);
                            }}>
                                {current.options.map((opt, i) => (
                                    <MUIFormControlLabel key={i} value={i} control={<Radio sx={{ color: BRAND.green }} />} label={<Typography sx={{ color: BRAND.white }}>{opt}</Typography>} sx={{ p: 2, my: 1, border: `1px solid ${BRAND.border}`, borderRadius: 2, "&:hover": { bgcolor: "rgba(255,255,255,0.02)" } }} />
                                ))}
                            </RadioGroup>
                            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                                <Button variant="outlined" disabled={activeIndex === 0} onClick={() => setActiveIndex(prev => prev - 1)} sx={{ color: BRAND.green, borderColor: BRAND.green }}>Previous</Button>
                                {activeIndex < 29 ? (
                                    <Button variant="contained" onClick={() => setActiveIndex(prev => prev + 1)} sx={{ bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 700 }}>Next Document</Button>
                                ) : (
                                    <Button variant="contained" onClick={() => handleSubmit()} sx={{ bgcolor: BRAND.greenBright, color: BRAND.bg, fontWeight: 900 }}>Finalize Submission</Button>
                                )}
                            </Stack>
                        </Paper>
                        <Alert icon={<GavelIcon sx={{ color: BRAND.green }} />} sx={{ bgcolor: "transparent", color: BRAND.muted, border: `1px solid ${BRAND.border}` }}>
                            System Integrity Active. Violations: {violations.length}/{EXAM_CONFIG.maxViolations}
                        </Alert>
                    </Stack>
                )}

                {/* 3. Official Results Transcript */}
                {submitted && results && (
                    <Paper sx={{ p: 6, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 6, textAlign: 'center' }}>
                        <Avatar sx={{ bgcolor: BRAND.green, width: 80, height: 80, mx: "auto", mb: 3 }}>
                            <FaTerminal size={40} color={BRAND.bg} />
                        </Avatar>
                        <Typography variant="h4" sx={{ color: BRAND.green, fontWeight: 900, mb: 1 }}>EXAM TRANSCRIPT</Typography>
                        <Typography variant="h1" sx={{ color: BRAND.white, fontWeight: 900, my: 4 }}>{results.percent}%</Typography>

                        <Grid container spacing={2} sx={{ textAlign: 'left', bgcolor: 'rgba(0,0,0,0.3)', p: 4, borderRadius: 4, border: `1px solid ${BRAND.border}` }}>
                            <Grid item xs={12} md={6}><Typography sx={{ color: BRAND.muted }}>Candidate:</Typography><Typography sx={{ color: BRAND.white }}>{results.candidate.fullName}</Typography></Grid>
                            <Grid item xs={12} md={6}><Typography sx={{ color: BRAND.muted }}>Track:</Typography><Typography sx={{ color: BRAND.white }}>{results.candidate.track}</Typography></Grid>
                            <Divider sx={{ width: '100%', my: 2, bgcolor: BRAND.border }} />
                            <Grid item xs={4}><Typography sx={{ color: BRAND.muted }}>Correct:</Typography><Typography sx={{ color: BRAND.green }}>{results.correct}</Typography></Grid>
                            <Grid item xs={4}><Typography sx={{ color: BRAND.muted }}>Violations:</Typography><Typography sx={{ color: results.violations > 0 ? BRAND.error : BRAND.green }}>{results.violations}</Typography></Grid>
                            <Grid item xs={4}><Typography sx={{ color: BRAND.muted }}>Status:</Typography><Typography sx={{ color: results.percent >= 70 ? BRAND.green : BRAND.error }}>{results.percent >= 70 ? "PASSED" : "FAILED"}</Typography></Grid>
                        </Grid>

                        <Button fullWidth variant="contained" sx={{ mt: 6, py: 2, bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 900 }} onClick={() => window.location.reload()}>Close and Return to Dashboard</Button>
                    </Paper>
                )}

                <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack({...snack, open: false})}>
                    <Alert severity="error" variant="filled">{snack.message}</Alert>
                </Snackbar>
            </Container>
        </Box>
    );
}
